/* eslint-disable */
/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  CustomCheckBox,
  RegularButton,
  ICommonDropdownOption,
  AlertAdapter,
  CommonDropdown,
  FullScreenLoading,
} from 'pg-ui-library';
import { useQuery } from '@tanstack/react-query';
import { IUser, ICompany } from 'src/interfaces';
import CompanyService from 'src/services/company.services';
import { useUpdateuser } from '../../mutations/user-setting.mutation';
import {
  ModalContent,
  AllContent,
  Header,
  MainContent,
  ModuleContainer,
  ModuleItem,
  CheckboxHeading,
  ModuleItemLabel,
  StatusContainer,
  StatusLabel,
  Footer,
  FormRow,
  FormRowHeading,
  CompanyGrid,
  CheckBoxContainer,
} from './styles';
import UserService from 'src/services/user.service';
import { useActions } from 'src/hooks/useActions';
import EmployeesService from 'src/services/employees.service';

export const activeOptions: ICommonDropdownOption[] = [
  { key: 'false', label: 'Inactive', value: 'false' },
  { key: 'true', label: 'Active', value: 'true' },
];

export const UserEditAdminModal: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: IUser;
  onSuccess: () => void;
}> = ({ isOpen, setIsOpen, user, onSuccess }) => {
  const [isDisconnectLoading, setIsDisconnectLoading] = useState(false);
  const updateUser = useUpdateuser();
  const formik = useFormik({
    initialValues: {
      userId: user?.id,
      isActive: user?.isActive ? 'true' : 'false',
      permission: {
        benefits: user?.permissionsUser.benefits || false,
        documents: user?.permissionsUser.documents || false,
        tax_forms: user?.permissionsUser.tax_forms || false,
        pay_history: user?.permissionsUser.pay_history || false,
        hr_profile: user?.permissionsUser.hr_profile || false,
        admin: user?.permissionsUser.admin || false,
        invoice: user?.permissionsUser.invoice || false,
        expense: user?.permissionsUser.expense || false,
        workflow: user?.permissionsUser.workflow || false,
        admin_employees: user?.permissionsUser.admin_employees || false,
        admin_pay_history: user?.permissionsUser.admin_pay_history || false,
        admin_year_end_forms: user?.permissionsUser.admin_year_end_forms || false,
        admin_benefits: user?.permissionsUser.admin_benefits || false,
        admin_reports: user?.permissionsUser.admin_reports || false,
        admin_bill_process: user?.permissionsUser.admin_bill_process || false,
        admin_pay_process: user?.permissionsUser.admin_pay_process || false,
        admin_override_permission: user?.permissionsUser.admin_override_permission || false,
        admin_support_desk: user?.permissionsUser.admin_support_desk || false,
        admin_leave: user?.permissionsUser.admin_leave || false,
        workflowTabs: {
          benefits: user?.permissionsUser.workflowTabs?.benefits || false,
          contacts: user?.permissionsUser.workflowTabs?.contacts || false,
        },
      },
      userCompanies: [] as string[],
    },
    validationSchema: yup.object().shape({
      permission: yup.object().shape({
        benefits: yup.boolean(),
        documents: yup.boolean(),
        tax_forms: yup.boolean(),
        pay_history: yup.boolean(),
        hr_profile: yup.boolean(),
        admin: yup.boolean(),
        workflow: yup.boolean(),
        admin_employees: yup.boolean(),
        admin_pay_history: yup.boolean(),
        admin_year_end_forms: yup.boolean(),
        admin_benefits: yup.boolean(),
        admin_reports: yup.boolean(),

        invoice: yup.boolean(),
        expense: yup.boolean(),
        admin_bill_process: yup.boolean(),
        admin_pay_process: yup.boolean(),
        admin_override_permission: yup.boolean(),

        admin_support_desk: yup.boolean(),
        admin_leave: yup.boolean(),
        workflowTabs: yup.object().shape({
          benefits: yup.boolean(),
          contacts: yup.boolean(),
        }),
      }),
    }),
    onSubmit: async values => {
      const updatedValues = {
        ...values,
        isActive: values.isActive === 'true',
      };
      try {
        await updateUser.mutateAsync({ updateUser: updatedValues });
        AlertAdapter('Updated successfully', 'success');
        setIsOpen(false);
        if (onSuccess) onSuccess();
      } catch (error) {
        const errorMsg = error?.toString() || 'An error occurred while updating the user';
        AlertAdapter(errorMsg, 'error');
      }
    },
  });

  const { patchEmployeeAccess, disconnectEmployeeAccess } = useActions();

  const disconnectEmployeeAccount = async () => {
    setIsDisconnectLoading(true);
    await EmployeesService.disconnectEmployee({ employeeId: user.employeeId, companyId: user.company })
      .then(() => {
        AlertAdapter('Admin account discontinued!', 'success');
      })
      .catch(err => {
        AlertAdapter('Error when disconnecting admin account', 'error');
      })
      .finally(() => {
        setIsDisconnectLoading(false);
        setIsOpen(!isOpen);
      });
  };

  const { isFetching, data: usersAccess } = useQuery(['admin/modules', user.id], async () => {
    const result = await UserService.getAdminModulesByUserId(user.id);
    return result;
  });

  const hasAdminPermission = (perm: string) =>
    usersAccess?.data?.some((p: any) => p.adminPermissionId === perm) ?? false;

  const { data } = useQuery({
    queryKey: ['users/companies'],
    queryFn: () => CompanyService.getCompanies(),
    enabled: true,
  });

  const { data: dataCompany, isLoading: companyLoading } = useQuery(
    ['users/companiesbyId', user.id],
    () => CompanyService.getCompaniesByUserID(user.id),
    {
      enabled: !!user.id,
    }
  );

  const { data: workflow } = useQuery({
    queryKey: ['users/workflow/id'],
    queryFn: () => CompanyService.getWorkflowById(user.id),
    enabled: true,
  });

  const companies = data?.data.items as ICompany[];

  const workflowData = workflow?.data;

  useEffect(() => {
    if (dataCompany && dataCompany.data) {
      const userCompanies = dataCompany.data.map((company: ICompany) => company.id);

      const isArray = Array.isArray(workflowData);

      const workflowTabs = {
        benefits: isArray && workflowData.includes('benefits'),
        contacts: isArray && workflowData.includes('contacts'),
      };

      formik.setValues({
        userId: user.id,
        isActive: user.isActive ? 'true' : 'false',
        permission: {
          benefits: user.permissionsUser.benefits,
          documents: user.permissionsUser.documents,
          tax_forms: user.permissionsUser.tax_forms,
          pay_history: user.permissionsUser.pay_history,
          hr_profile: user.permissionsUser.hr_profile,
          admin: user.permissionsUser.admin,
          invoice: user.permissionsUser.invoice,
          expense: user?.permissionsUser.expense,
          admin_employees: hasAdminPermission('admin_employees'),
          admin_pay_history: hasAdminPermission('admin_pay_history'),
          admin_year_end_forms: hasAdminPermission('admin_year_end_forms'),
          admin_benefits: hasAdminPermission('admin_benefits'),
          admin_reports: hasAdminPermission('admin_reports'),

          admin_bill_process: hasAdminPermission('admin_bill_process'),
          admin_pay_process: hasAdminPermission('admin_pay_process'),
          admin_override_permission: hasAdminPermission('admin_override_permission'),

          admin_support_desk: hasAdminPermission('admin_support_desk'),
          admin_leave: hasAdminPermission('admin_leave'),
          workflow: isArray && workflowData.length > 0,
          workflowTabs,
        },
        userCompanies,
      });
    }
  }, [user, dataCompany, workflowData, usersAccess]);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={() => setIsOpen(false)}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '760px',
          height: '580px',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          background: '#F3F4F8',
          overflowY: 'auto',
        },
      }}
    >
      {companyLoading ? (
        <FullScreenLoading />
      ) : (
        <ModalContent>
          {!companyLoading && (
            <>
              <Header>
                <h3>Edit Admin User</h3>
              </Header>
              <AllContent
                style={{
                  overflowY: 'auto',
                  padding: '24px',
                  maxHeight: 'calc(580px - 130px)',
                }}
              >
                <MainContent>
                  <ModuleContainer>
                    <ModuleItemLabel>ESS Modules</ModuleItemLabel>
                    <ModuleItem>
                      <CustomCheckBox
                        id="benefits"
                        label=""
                        name="permission.benefits"
                        checked={formik.values.permission.benefits}
                        onChange={() => formik.setFieldValue('permission.benefits', !formik.values.permission.benefits)}
                      />
                      <CheckboxHeading>Benefits</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        id="pay_history"
                        label=""
                        name="permission.pay_history"
                        checked={formik.values.permission.pay_history}
                        onChange={() =>
                          formik.setFieldValue('permission.pay_history', !formik.values.permission.pay_history)
                        }
                      />
                      <CheckboxHeading>Pay History</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        id="admin"
                        label=""
                        name="permission.admin"
                        checked={formik.values.permission.admin}
                        onChange={() => formik.setFieldValue('permission.admin', !formik.values.permission.admin)}
                      />
                      <CheckboxHeading>Admin</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        id="hr_profile"
                        label=""
                        name="permission.hr_profile"
                        checked={formik.values.permission.hr_profile}
                        onChange={() =>
                          formik.setFieldValue('permission.hr_profile', !formik.values.permission.hr_profile)
                        }
                      />
                      <CheckboxHeading>HR Profile</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        id="documents"
                        label=""
                        name="permission.documents"
                        checked={formik.values.permission.documents}
                        onChange={() =>
                          formik.setFieldValue('permission.documents', !formik.values.permission.documents)
                        }
                      />
                      <CheckboxHeading>Documents</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        label=""
                        id="tax_forms"
                        name="permission.tax_forms"
                        checked={formik.values.permission.tax_forms}
                        onChange={() =>
                          formik.setFieldValue('permission.tax_forms', !formik.values.permission.tax_forms)
                        }
                      />
                      <CheckboxHeading>Expense</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        label=""
                        id="expense"
                        name="permission.expense"
                        checked={formik.values.permission.expense}
                        onChange={() => formik.setFieldValue('permission.expense', !formik.values.permission.expense)}
                      />
                      <CheckboxHeading>Invoice</CheckboxHeading>
                    </ModuleItem>
                    <ModuleItem>
                      <CustomCheckBox
                        label=""
                        id="invoice"
                        name="permission.invoice"
                        checked={formik.values.permission.invoice}
                        onChange={() => formik.setFieldValue('permission.invoice', !formik.values.permission.invoice)}
                      />
                      <CheckboxHeading>Tax Forms</CheckboxHeading>
                    </ModuleItem>
                  </ModuleContainer>
                  <StatusContainer>
                    <StatusLabel>Status</StatusLabel>
                    <div style={{ minWidth: '100%', marginTop: '10%' }}>
                      <CommonDropdown
                        label=""
                        id="isActive"
                        name="isActive"
                        options={activeOptions}
                        value={formik.values.isActive}
                        setValue={(value: string) => formik.setFieldValue('isActive', value)}
                        selectStyle={{
                          width: '100%',
                          marginTop: '8px',
                          height: 'auto',
                        }}
                      />
                    </div>
                  </StatusContainer>
                </MainContent>
                <ModuleContainer style={{ marginBlock: '50px' }}>
                  <ModuleItemLabel>Admin Modules</ModuleItemLabel>
                  <ModuleItem>
                    <CustomCheckBox
                      id="admin_employees"
                      label=""
                      name="permission.admin_employees"
                      checked={formik.values.permission.admin_employees}
                      onChange={() =>
                        formik.setFieldValue('permission.admin_employees', !formik.values.permission.admin_employees)
                      }
                    />
                    <CheckboxHeading>Employees</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      id="admin_pay_history"
                      label=""
                      name="permission.admin_pay_history"
                      checked={formik.values.permission.admin_pay_history}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_pay_history',
                          !formik.values.permission.admin_pay_history
                        )
                      }
                    />
                    <CheckboxHeading>Pay History</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      id="admin_year_end_forms"
                      label=""
                      name="permission.admin_year_end_forms"
                      checked={formik.values.permission.admin_year_end_forms}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_year_end_forms',
                          !formik.values.permission.admin_year_end_forms
                        )
                      }
                    />
                    <CheckboxHeading>Year-end forms</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      id="admin_benefits"
                      label=""
                      name="permission.admin_benefits"
                      checked={formik.values.permission.admin_benefits}
                      onChange={() =>
                        formik.setFieldValue('permission.admin_benefits', !formik.values.permission.admin_benefits)
                      }
                    />
                    <CheckboxHeading>Benefits</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      id="admin_support_desk"
                      label=""
                      name="permission.admin_support_desk"
                      checked={formik.values.permission.admin_support_desk}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_support_desk',
                          !formik.values.permission.admin_support_desk
                        )
                      }
                    />
                    <CheckboxHeading>Support Desk</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      label=""
                      id="admin_leave"
                      name="permission.admin_leave"
                      checked={formik.values.permission.admin_leave}
                      onChange={() =>
                        formik.setFieldValue('permission.admin_leave', !formik.values.permission.admin_leave)
                      }
                    />
                    <CheckboxHeading>Leave</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      label=""
                      id="admin_reports"
                      name="permission.admin_reports"
                      checked={formik.values.permission.admin_reports}
                      onChange={() =>
                        formik.setFieldValue('permission.admin_reports', !formik.values.permission.admin_reports)
                      }
                    />
                    <CheckboxHeading>Reports</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      label=""
                      id="admin_bill_process"
                      name="permission.admin_bill_process"
                      checked={formik.values.permission.admin_bill_process}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_bill_process',
                          !formik.values.permission.admin_bill_process
                        )
                      }
                    />
                    <CheckboxHeading>Bill Process</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      label=""
                      id="admin_pay_process"
                      name="permission.admin_pay_process"
                      checked={formik.values.permission.admin_pay_process}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_pay_process',
                          !formik.values.permission.admin_pay_process
                        )
                      }
                    />
                    <CheckboxHeading>Pay Process</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <CustomCheckBox
                      label=""
                      id="admin_override_permission"
                      name="permission.admin_override_permission"
                      checked={formik.values.permission.admin_override_permission}
                      onChange={() =>
                        formik.setFieldValue(
                          'permission.admin_override_permission',
                          !formik.values.permission.admin_override_permission
                        )
                      }
                    />
                    <CheckboxHeading>Override</CheckboxHeading>
                  </ModuleItem>
                  <ModuleItem>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginLeft: '-10px' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CustomCheckBox
                            id="workflow"
                            label=""
                            name="permission.workflow"
                            checked={formik.values.permission.workflow}
                            onClick={() =>
                              formik.setFieldValue('permission.workflow', !formik.values.permission.workflow)
                            }
                            onChange={() =>
                              formik.setFieldValue('permission.workflow', !formik.values.permission.workflow)
                            }
                          />
                          <CheckboxHeading>Workflow</CheckboxHeading>
                        </div>
                        <span
                          onClick={() =>
                            formik.setFieldValue('permission.workflow', !formik.values.permission.workflow)
                          }
                          style={{
                            fontSize: '1.2rem',
                            transform: formik.values.permission.workflow ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            color: '#4948AC',
                            marginTop: '3px',
                          }}
                        >
                          ▶
                        </span>
                      </div>
                      {formik.values.permission.workflow && (
                        <div
                          style={{
                            marginTop: '10px',
                            marginLeft: '32px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            padding: '10px 14px',
                            backgroundColor: '#f4f5fb',
                            borderLeft: '3px solid #4948AC',
                            borderRadius: '4px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CustomCheckBox
                              id="workflowTabs.benefits"
                              label=""
                              name="permission.workflowTabs.benefits"
                              checked={formik.values.permission.workflowTabs.benefits}
                              onChange={() =>
                                formik.setFieldValue(
                                  'permission.workflowTabs.benefits',
                                  !formik.values.permission.workflowTabs.benefits
                                )
                              }
                            />
                            <CheckboxHeading style={{ fontWeight: 400, fontSize: '1.2rem' }}>Benefits</CheckboxHeading>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CustomCheckBox
                              id="workflowTabs.contacts"
                              label=""
                              name="permission.workflowTabs.contacts"
                              checked={formik.values.permission.workflowTabs.contacts}
                              onChange={() =>
                                formik.setFieldValue(
                                  'permission.workflowTabs.contacts',
                                  !formik.values.permission.workflowTabs.contacts
                                )
                              }
                            />
                            <CheckboxHeading style={{ fontWeight: 400, fontSize: '1.2rem' }}>Contacts</CheckboxHeading>
                          </div>
                        </div>
                      )}
                    </div>
                  </ModuleItem>
                </ModuleContainer>
                <FormRowHeading>Company</FormRowHeading>
                <FormRow>
                  <CompanyGrid>
                    {companies.map(company => {
                      const isChecked = formik.values.userCompanies.includes(company.id);
                      return (
                        <CheckBoxContainer key={company.id}>
                          <CustomCheckBox
                            label=""
                            id={company.id}
                            name={company.legalEntityId}
                            checked={isChecked}
                            onChange={() => {
                              const selected = formik.values.userCompanies;
                              if (selected.includes(company.id)) {
                                formik.setFieldValue(
                                  'userCompanies',
                                  selected.filter(id => id !== company.id)
                                );
                              } else {
                                formik.setFieldValue('userCompanies', [...selected, company.id]);
                              }
                            }}
                          />
                          <span>{company.legalEntityId}</span>
                        </CheckBoxContainer>
                      );
                    })}
                  </CompanyGrid>
                </FormRow>
                <Footer>
                  <div>
                    <RegularButton
                      disabled={isDisconnectLoading}
                      style={{ marginRight: '140px' }}
                      onClick={disconnectEmployeeAccount}
                    >
                      Discontinue Admin Account
                    </RegularButton>
                  </div>
                  <div>
                    <RegularButton
                      type="button"
                      onClick={() => setIsOpen(false)}
                      style={{ color: '#4948AC', backgroundColor: '#fff' }}
                    >
                      Cancel
                    </RegularButton>
                    <RegularButton
                      type="submit"
                      onClick={() => formik.handleSubmit()}
                      disabled={formik.isSubmitting || !formik.isValid}
                    >
                      Save
                    </RegularButton>
                  </div>
                </Footer>
              </AllContent>
            </>
          )}
        </ModalContent>
      )}
    </ReactModal>
  );
};
